import { Dispatch } from 'redux';
import { AxiosResponse } from 'axios';
import { DispatchFunction, IUser, IActiveFilter, IUserDetail, IGrants, IUserAttribute } from '@app/models';
import {
    GET_ALL_USERS,
    GET_ALL_USERS_LOADING,
    GET_USER,
    ADD_USER,
    IS_PRINCIPAL_NAME_AVAILABLE,
    RESET_USER_PASSWORD,
    SET_USER_APNS,
    USERS_COUNT,
    EXPIRE_USER_PASSWORD,
    REMOVE_ROLE_MEMBERSHIP,
    IMPORT_USERS,
    REMOVE_GROUP_MEMBERSHIP,
    REVOKE_PUSH,
    REVOKE_U2F,
    REVOKE_OTP,
    IMPORT_USERS_ERROR,
    UPDATE_USER_ATTRIBUTE,
    UPDATE_USER_ATTRIBUTES,
    UPDATE_USER_ATTRIBUTE_RESET,
    DELETE_USER_ATTRIBUTE,
    SET_USER_PATCH_DATA,
    ADD_ROLES_MEMBERSHIP,
    ADD_GROUPS_MEMBERSHIP,
    REMOVE_EXPIRE_USER_PASSWORD_RESPONSE
} from '@app/store/constants';
import { usersService } from '@app/services';
import { loadingData } from './basic.actions';
import { getModalSwitch, getLoaderBtn, getConfirmModalSwitch } from '.';
import { toast } from 'react-toastify';


export function handleGetUsersData(
    orderBy: string,
    asc: string,
    top: number,
    skip: number,
    organizationId: string,
    isReset: boolean,
    filters?: IActiveFilter[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        dispatch(loadingData(true));

        if (isReset) {
            dispatch(usersCount(0));
            dispatch(getAllUsers([]));
        }
        usersService.getUsers(orderBy, asc, top, skip, organizationId, isReset, filters)
            .then((response: AxiosResponse) => {
                dispatch(getAllUsers(response.data.value));

                if (isReset) {
                    dispatch(usersCount(response.data['@odata.count']));
                }

                dispatch(loadingData(false));
            })
            .catch(() => {
                dispatch(loadingData(false));
            });
    });
}

export function handleImportUsers(
    fileData: object,
    orgId: string,
    policyId: string
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.importUsers(fileData, orgId, policyId)
            .then((response: AxiosResponse) => {
                dispatch(addImportUsersData(response.data));
            })
            .then(() => {
                dispatch(getImportUsersError(false));
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .catch((error) => {
                dispatch(getLoaderBtn(false));
                dispatch(getImportUsersError(true));
            });
    });
}

export function handleAddRolesMembership(
    userId: string,
    rolesMembership: any[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.addRoleForUser(userId, rolesMembership)
            .then((response: AxiosResponse) => {
                dispatch(addRolesMembership(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                usersService.getUser(userId)
                    .then((response: AxiosResponse) => {
                        dispatch(getUser(response.data));
                        dispatch(loadingData(true));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    });
}

export function handleAddGroupsMembership(
    userId: string,
    groupMembership: any[]
): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.addGroupForUser(userId, groupMembership)
            .then((response: AxiosResponse) => {
                dispatch(addGroupsMembership(response.data));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
                dispatch(getConfirmModalSwitch(true));
            })
            .then(() => {
                usersService.getUser(userId)
                    .then((response: AxiosResponse) => {
                        dispatch(getUser(response.data));
                        dispatch(loadingData(true));
                    })
                    .then(() => dispatch(loadingData(false)));
            })
            .catch((error) => toast.error(error));
    });
}

export function handleIsPrincipalNameAvailable(organizationId: string, principalName: string, tenantId: null): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.isPrincipalNameAvailable(organizationId, principalName, tenantId)
            .then((response: AxiosResponse) => {
                dispatch(addIsPrincipal(response.data));
            });
    });
}

export function handleGetUserData(userId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        dispatch(loadingData(true));
        usersService.getUser(userId)
            .then((response: AxiosResponse) => {
                dispatch(getUser(response.data));
            })
            .then(() => dispatch(loadingData(false)));
    });
}

export function handleResetUserPassword(userId: string, password: string, expired: boolean): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.resetUserPassword(userId, password, expired)
            .then((response: AxiosResponse) => {
                dispatch(resetUserPasswordResponseAction(response));
            });
    });
}

export function handleExpireUserPassword(userId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.setUserPassword(userId)
            .then((response: AxiosResponse) => {
                dispatch(expireUserPasswordResponseAction(response));
            });
    });
}

export function handleRemoveRoleMembership(roleId: string, userId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.removeRoleMembership(roleId, userId)
            .then((response: AxiosResponse) => {
                dispatch(removeRoleMembershipResponseAction(response));
            });
    });
}

export function handleRemoveGroupMembership(groupId: string, userId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.removeGroupMembership(groupId, userId)
            .then((response: AxiosResponse) => {
                dispatch(removeGroupMembershipResponseAction(response));
            });
    });
}

export function handleRevokePush(userId: string, pushTokenId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.deleteUserPushToken(userId, pushTokenId)
            .then((response: AxiosResponse) => {
                dispatch(revokePushResponseAction(response));
            });
    });
}

export function handleRevokeU2f(userId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.deleteUserU2fToken(userId)
            .then((response: AxiosResponse) => {
                dispatch(revokeU2fResponseAction(response));
            });
    });
}

export function handleRevokeOtp(userId: string): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.deleteUserOtpToken(userId)
            .then((response: AxiosResponse) => {
                dispatch(revokeOtpResponseAction(response));
            });
    });
}

export function handleUpdateUserAttribute(
    userId: string,
    attributeId: string,
    attributeKey: string,
    attributeValue: string,
    handleError: Function
): DispatchFunction {
    return (dispatch: Dispatch) => {
        usersService
            .updateAttribute(userId, attributeId, attributeKey, attributeValue, handleError)
            .then((response: AxiosResponse) => {
                dispatch(updateUserAttributeResponseAction(response, attributeId, attributeKey, attributeValue));

                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));

                dispatch(updateUserAttributeClearState());
            });
    };
}

export function handleAddUserAttribute(userId: string, attributeKey: string, attributeValue: string, handleError: Function): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.createAttribute(userId, attributeKey, attributeValue, handleError)
            .then((response: AxiosResponse) => {
                if (response.status !== 204) {
                    dispatch(getLoaderBtn(false));
                }
                return usersService.getUser(userId);
            })
            .then((response: AxiosResponse) => {
                dispatch(addUserAttributeResponseAction(response.data.Attributes));

                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));

                dispatch(updateUserAttributeClearState());
            });
    });
}

export function handleDeleteUserAttribute(userId: string, attributeId: string, handleError: Function): DispatchFunction {
    return ((dispatch: Dispatch) => {
        usersService.deleteAttribute(userId, attributeId, handleError)
            .then((response: AxiosResponse) => {
                dispatch(deleteUserAttributeResponseAction(response, attributeId));
            });
    });
}

export function getAllUsers(users: IUser[]) {
    return {
        payload: users,
        type: GET_ALL_USERS
    };
}

export function usersCount(count: number) {
    return {
        payload: count,
        type: USERS_COUNT
    };
}

export function getAllUsersLoading(loading: boolean) {
    return {
        payload: loading,
        type: GET_ALL_USERS_LOADING
    };
}

export function addUserData(userData: object) {
    return {
        payload: userData,
        type: ADD_USER
    };
}

export function addImportUsersData(importUsersData: object) {
    return {
        payload: importUsersData,
        type: IMPORT_USERS
    };
}

export function getImportUsersError(importUsersError: boolean) {
    return {
        payload: importUsersError,
        type: IMPORT_USERS_ERROR
    };
}

export function addIsPrincipal(isPrincipal: string) {
    return {
        payload: isPrincipal,
        type: IS_PRINCIPAL_NAME_AVAILABLE
    };
}

export function getUser(user: IUserDetail) {
    user.Grants.forEach(function (d: IGrants) {
        if (d.Thumbprint !== undefined && d.Thumbprint !== '') {
            d.ThumbprintAsObject = JSON.parse(d.Thumbprint);
        }
    });

    return {
        payload: user,
        type: GET_USER
    };
}

export function resetUserPasswordResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: RESET_USER_PASSWORD
    };
}

export function expireUserPasswordResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: EXPIRE_USER_PASSWORD
    };
}

export function removeExpireUserPasswordResponseAction() {
    return {
        payload: '',
        type: REMOVE_EXPIRE_USER_PASSWORD_RESPONSE
    };
}

export function removeRoleMembershipResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: REMOVE_ROLE_MEMBERSHIP
    };
}

export function removeGroupMembershipResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: REMOVE_GROUP_MEMBERSHIP
    };
}

export function revokePushResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: REVOKE_PUSH
    };
}

export function revokeU2fResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: REVOKE_U2F
    };
}

export function revokeOtpResponseAction(response: AxiosResponse<any>) {
    return {
        payload: response,
        type: REVOKE_OTP
    };
}

export function updateUserAttributeResponseAction(response: AxiosResponse<any>, attributeId: string, attributeKey: string, attributeValue: string) {
    const returnedData: string = '{ "Id": "' + attributeId + '", "Key": "' + attributeKey + '", "Value": "' + attributeValue + '"  }';

    return {
        payload: response,
        type: UPDATE_USER_ATTRIBUTE,
        importantData: returnedData
    };
}

export function addUserAttributeResponseAction(attributes: IUserAttribute[]) {
    return {
        payload: attributes,
        type: UPDATE_USER_ATTRIBUTES
    };
}

export function updateUserAttributeClearState() {
    return {
        payload: '',
        type: UPDATE_USER_ATTRIBUTE_RESET
    };
}

export function deleteUserAttributeResponseAction(response: AxiosResponse<any>, attributeId: string) {
    return {
        payload: response,
        type: DELETE_USER_ATTRIBUTE,
        importantData: attributeId
    };
}

export function setUserPatchData(userPatchObject: any) {
    return {
        payload: userPatchObject,
        type: SET_USER_PATCH_DATA
    };
}

export function setUserApns(userApnList: any[]) {
    return {
        payload: userApnList,
        type: SET_USER_APNS
    };
}

export function addRolesMembership(rolesMembership: any[]) {
    return {
        payload: rolesMembership,
        type: ADD_ROLES_MEMBERSHIP
    };
}

export function addGroupsMembership(groupMembership: any[]) {
    return {
        payload: groupMembership,
        type: ADD_GROUPS_MEMBERSHIP
    };
}